import { parseISO } from "date-fns";
import _ from "lodash";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { FormInstanceDataTypeEnum } from "../../common/enums/FormInstanceDataTypeEnum";
import { IAttachment } from "../../common/models/IAttachment";
import { FormControls, FormValueType, IBaseFormControl, IFormDateRange, IFormGrid, IFormSection, IFormTemplate, IFormTextField } from "../../services/custom-forms-data-service/TemplateModels";
import { FormFeatureEnum, FormValueDataType, IFormInstanceData, IFormInstanceHistory, IFormInstanceValue } from "../../views/object-form-view/Models";
import { RowDef } from "../ag-grid-extensions/models/RowDef";
import CustomFormGridUtils from "./renderers/CustomFormGridUtils";


export const FORM_CREATED_SIGNATURE_CONTROL = "_createdSignature";
export const FORM_CLOSED_SIGNATURE_CONTROL = "_closedSignature";


export type CustomFormType = {
    attachments: IAttachment[];

} & Record<string, string | number | boolean | Date | IAttachment[]>;

export type FormulaType = { output: string, func: Function };
export type FormulasType = Record<string, Array<FormulaType>>;

const _addRecordProperty = (record: Record<string, any>, control: IBaseFormControl, instance?: IFormInstanceHistory) => {

    if (control.controlType !== 'section' && control.controlType !== 'label' && control.controlType !== 'attachments') {

        const value = instance?.values.find(o => o.name === control.name)?.value;

        if (control.controlType === 'daterange') {

            record[`${control.name}.startDate`] = value ? 
                                                parseISO(value as string) : 
                                                control.defaultValue ? 
                                                parseISO(control.defaultValue?.toString()) : 
                                                new Date();
            record[`${control.name}.endDate`] = value ? 
                                                parseISO(value as string) : 
                                                control.defaultValue ? 
                                                parseISO(control.defaultValue?.toString()) : 
                                                new Date();
            return;
        }

        switch (control.valueType.toLowerCase()) {
            case 'date':
                record[control.name] = value ? 
                                        parseISO(value as string) : 
                                        control.defaultValue ? 
                                        parseISO(control.defaultValue?.toString()) : 
                                        new Date();
                break;

            case 'boolean':
                record[control.name] = value as boolean ?? control.defaultValue ?? false;
                break;

            case 'number':
                record[control.name] = value as number ?? control.defaultValue ?? '';
                break;

            case 'string':
                record[control.name] = value as string ?? control.defaultValue ?? '';
                break;

            default:
                console.log(`record property support not implemented for ${control.valueType}`);
                break;
        }

    }
};

const mapValueTypeToDataType = (o: FormValueType): FormInstanceDataTypeEnum => {
    switch (o.toLowerCase()) {
        case 'string':
            return FormInstanceDataTypeEnum.String;
        case 'number':
            return FormInstanceDataTypeEnum.Double;
        case 'boolean':
            return FormInstanceDataTypeEnum.Boolean;
        case 'date':
            return FormInstanceDataTypeEnum.Date;
        default:
            return FormInstanceDataTypeEnum.String;
    }
};

const getFormInstanceValue = (item: IBaseFormControl,
    //values: Record<string, string | number | Date | boolean | IAttachment[]>) : 
    values: CustomFormType):
    IFormInstanceValue<FormValueDataType> => {

    const dataType = mapValueTypeToDataType(item.valueType);

    return {
        name: item.name,
        dataType: dataType,
        value: dataType === FormInstanceDataTypeEnum.Double
            ? Number.parseFloat(values[item.name] as string)
            : values[item.name]

    } as IFormInstanceValue<FormValueDataType>;
};

const getDateRangeFormInstanceValue = (item: IFormDateRange,
    //values: Record<string, string | number | Date | boolean | IAttachment[]>): 
    values: CustomFormType):
    [IFormInstanceValue<FormValueDataType>, IFormInstanceValue<FormValueDataType>] => {

    const startDate = {
        name: `${item.name}.startDate`,
        dataType: mapValueTypeToDataType(item.valueType),
        value: values[`${item.name}.startDate`]
    } as IFormInstanceValue<FormValueDataType>;

    const endDate = {
        name: `${item.name}.endDate`,
        dataType: mapValueTypeToDataType(item.valueType),
        value: values[`${item.name}.endDate`]
    } as IFormInstanceValue<FormValueDataType>;

    return [startDate, endDate];
};

const getGridFormInstanceValues = (item: IFormGrid,
    rows: RowDef[],
    currentRows: RowDef[]):
    IFormInstanceValue<Array<IFormInstanceValue<FormValueDataType>[]>> => {

    let formValue: IFormInstanceValue<Array<IFormInstanceValue<FormValueDataType>[]>> = {
        name: item.name,
        dataType: FormInstanceDataTypeEnum.Grid,
        value: new Array<IFormInstanceValue<FormValueDataType>[]>()
    };

    currentRows.forEach(o => {

        const rowInstanceData: IFormInstanceValue<FormValueDataType>[] = [];

        let rowToSave = o;

        const changedRow = rows.find(v => v.id === o.id);
        if (changedRow && !_.isEqual(o, changedRow)) {
            rowToSave = changedRow;
        }

        item.columns.forEach(v => {
            const cellValue: IFormInstanceValue<FormValueDataType> = {
                name: v.field,
                dataType: mapValueTypeToDataType(v.valueType),
                value: rowToSave[v.field]
            };

            rowInstanceData.push(cellValue);
        });

        formValue.value.push(rowInstanceData);
    });

    return formValue;
};

const _getLatestHistory = (instance: IFormInstanceData | undefined) => {
    // use the most recent history data
    return instance ? instance.history.sort((a, b) => (b.modifiedTimestamp.valueOf() ?? 0) - (a.modifiedTimestamp.valueOf() ?? 0))[0] : undefined;
};

const _addFormula = (textField: IFormTextField, formulas: Record<string, Array<FormulaType>>) => {
    if (textField.formula) {
        const regex = /{(.*?)}/g;
        const output = textField.name;
        const inputs = [...textField.formula.matchAll(regex)];
        const variables = inputs.map(input => input[1]);
        const declarations = variables.map(v => `const ${v}=Number.parseFloat(getValues("${v}"));`).join(" ");
        const logicalExp = inputs.map(input => `Number.isFinite(${input[1]})`).join(" && ");
        const calculation = textField.formula.replace(regex, (_match, p1) => {
            return p1;
        });
        const body = `${declarations}
        if(${logicalExp}) {
            return ${calculation};
        }
        return undefined;`;
        
        // eslint-disable-next-line no-new-func
        const func = new Function("getValues", body);

        variables.forEach(variable => {
            const key = variable;

            if (formulas.hasOwnProperty(key)) {
                formulas[key].push({output: output, func: func});

            } else {
                formulas[key] = new Array<FormulaType>();
                formulas[key].push({output: output, func: func});
            }
        });
    }
}

const _getAllControls = (formTemplate: IFormTemplate): FormControls[] => {

    const controls = formTemplate.template.controls
    .filter(o => o.controlType !== 'section')
    .concat(formTemplate.template
        .controls
        .filter(o => o.controlType === 'section')
        .flatMap(o => (o as IFormSection).controls)
    );


    return controls;

}

const CustomFormRunnerUtils = {

    getLatestHistory: (instance: IFormInstanceData | undefined): IFormInstanceHistory | undefined => {

        return _getLatestHistory(instance);
    },

    mapToForm: (data: IFormTemplate,
        instance?: IFormInstanceData,
        objectType?: eLynxObjectTypeEnum): CustomFormType => {

        // use the most recent history data
        const history = _getLatestHistory(instance);

        //const formData: Record<string, string | number | boolean | Date | IAttachment[]> = {};
        const formData: CustomFormType = {
            attachments: []
        };

        data.template.controls.forEach(o => {
            if (o.controlType === 'section') {
                const section = o as IFormSection;
                section.controls.forEach(v => _addRecordProperty(formData, v as IBaseFormControl, history));

            } else if (o.controlType === 'grid' || o.controlType === 'attachments') {
                // nothing for grid, it will be handled manually
            } else {
                _addRecordProperty(formData, o as IBaseFormControl, history);
            }
        });

        if (objectType === eLynxObjectTypeEnum.Group && !instance) {
            formData['objectkey'] = '';
        }

        formData['closeform'] = false;

        if ((data.formFeatures & FormFeatureEnum.allowAttachments) > 0) {

            formData.attachments = instance ? instance.attachments : new Array<IAttachment>();
        }


        return formData;
    },

    mapFormulas: (data: IFormTemplate): FormulasType => {

        const formulas: FormulasType = {};

        data.template.controls.forEach(o => {
            if (o.controlType === 'section') {
                const section = o as IFormSection;
                section.controls.forEach(v => {
                    // if this control has a formula, add it to the formula list
                    if (v.controlType === "textfield") {
                        const textField = v as IFormTextField;

                        _addFormula(textField, formulas);
                    }
                });

            } else if (o.controlType === 'grid') {
                // nothing for grid, it will be handled manually
            } else {
                // if this control has a formula, add it to the formula list
                if (o.controlType === "textfield") {
                    const textField = o as IFormTextField;

                    _addFormula(textField, formulas);
                }
            }
        });

        return formulas;
    },

    getFormInstanceValues: (formTemplate: IFormTemplate,
        currentInstanceData: IFormInstanceData | undefined,
        //values: Record<string, string | number | Date | boolean | IAttachment[]>,
        values: CustomFormType,
        gridData: RowDef[]):
        IFormInstanceValue<FormValueDataType | Array<IFormInstanceValue<FormValueDataType>[]>>[] => {

        let formValues: IFormInstanceValue<FormValueDataType | Array<IFormInstanceValue<FormValueDataType>[]>>[] = [];

        const controls = formTemplate.template.controls
            .filter(o => o.controlType !== 'section')
            .concat(formTemplate.template
                .controls
                .filter(o => o.controlType === 'section')
                .flatMap(o => (o as IFormSection).controls)
            )
            .filter(o => o.controlType !== 'label' && o.controlType !== 'attachments');

        controls.forEach(o => {
            if (o.controlType === 'daterange') {
                const [start, end] = getDateRangeFormInstanceValue(o as IFormDateRange, values);
                formValues.push(start);
                formValues.push(end);
            } else if (o.controlType === 'grid') {

                //const lastHistory = _getLatestHistory(currentInstanceData);
                //const gridFormValue = lastHistory?.values.find(o => o.name === o.name);
                //
                //                const currentValues = gridFormValue?.value ? gridFormValue.value as  Array<IFormInstanceValue<FormValueDataType>[]> : new  Array<IFormInstanceValue<FormValueDataType>[]>();


                //const v = getGridFormInstanceValues(o as IFormGrid, gridData, currentValues);
                const currentRows = CustomFormGridUtils.createRows(o as IFormGrid, currentInstanceData);
                const v = getGridFormInstanceValues(o as IFormGrid, gridData, currentRows);

                formValues.push(v);
            } else {

                const v = getFormInstanceValue(o as IBaseFormControl, values);
                if (v) {
                    formValues.push(v);
                }
            }
        });

        return formValues;
    },
    hasAttachmentsControl: (formTemplate: IFormTemplate): boolean => {
        const controls = _getAllControls(formTemplate);
        return controls.find(o => o.controlType === 'attachments') !== undefined;
    },
    findFormControl: (formTemplate: IFormTemplate, controlName: string): FormControls | undefined => {
        const controls = _getAllControls(formTemplate);
        return controls.find(o => (o as IBaseFormControl)?.name === controlName);
    }


};

export default CustomFormRunnerUtils;